import React from "react";
import useIsMobile from "@/hooks/useIsMobile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  BottomSheet,
  BottomSheetFooter,
  BottomSheetClose,
} from "@/components/ui/bottom-sheet";
import { cn } from "@/lib/utils";

/**
 * ResponsiveModal — Dialog on desktop, BottomSheet on mobile.
 * Single API regardless of surface. Focus-trap, Escape-close, swipe-down-dismiss on mobile.
 *
 * Usage:
 *   <ResponsiveModal open={open} onOpenChange={setOpen} title="Edit Brief" description="...">
 *     <form onSubmit={onSubmit}>
 *       ...fields...
 *       <ModalFooter>
 *         <ModalClose>Cancel</ModalClose>
 *         <button type="submit">Save</button>
 *       </ModalFooter>
 *     </form>
 *   </ResponsiveModal>
 */
export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  size = "md", // "sm" | "md" | "lg" | "xl"
}) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        description={description}
        className={className}
      >
        {children}
      </BottomSheet>
    );
  }

  const widthClass = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  }[size] || "max-w-lg";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          widthClass,
          "bg-[var(--s1)] border-[var(--bdr)] text-[var(--t1)]",
          className
        )}
      >
        {(title || description) && (
          <DialogHeader>
            {title && <DialogTitle className="font-heading">{title}</DialogTitle>}
            {description && (
              <DialogDescription className="text-[var(--t3)]">{description}</DialogDescription>
            )}
          </DialogHeader>
        )}
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function ModalFooter({ children, className }) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return <BottomSheetFooter className={className}>{children}</BottomSheetFooter>;
  }
  return (
    <DialogFooter className={cn("mt-4 gap-2", className)}>{children}</DialogFooter>
  );
}

export function ModalClose({ children, asChild, className, ...props }) {
  const isMobile = useIsMobile();
  const Component = isMobile ? BottomSheetClose : DialogClose;
  return (
    <Component asChild={asChild} className={className} {...props}>
      {children}
    </Component>
  );
}

export default ResponsiveModal;
